import { useParams } from 'react-router-dom';
import DashboardLayout from '../components/layouts/DashboardLayout';
import StatusBadge from '../components/ui/StatusBadge';
import { useApplication } from '../hooks/usePipeline';
import { useAuth } from '../hooks/useAuth';
import type { Application } from '../data/mock';

function getTimeline(app: Application) {
  return [
    { label: 'Applied', done: true, detail: null, pending: false },
    { label: 'Recruiters Found', done: app.outreach_count > 0, detail: app.outreach_count > 0 ? `Found ${app.outreach_count} recruiters` : null, pending: false },
    { label: 'Drafts Created', done: app.draft_count > 0 || app.sent_count > 0, detail: (app.draft_count > 0 || app.sent_count > 0) ? `${app.outreach_count} drafts in your Gmail` : null, pending: false },
    { label: 'Outreach Sent', done: app.sent_count > 0, detail: app.sent_count > 0 ? `${app.sent_count} of ${app.outreach_count} sent` : null, pending: app.sent_count > 0 && app.sent_count < app.outreach_count },
    { label: 'Replies', done: app.replied_count > 0, detail: app.replied_count > 0 ? `${app.replied_count} ${app.replied_count === 1 ? 'reply' : 'replies'} received` : null, pending: false },
  ];
}

const sampleEmailBody = (company: string, role: string, senderName: string) => `Hi [Name],

I recently applied for the ${role} role at ${company} and wanted to reach out directly.

At my last role, I led the rebuild of a $2B institutional trading platform — focusing on uptime, scale, and cross-team coordination. I also spearheaded a cloud migration that saved $40K/quarter.

I'd love the chance to connect if you're scheduling conversations for this role.

Thanks for your time,
${senderName}`;

export default function ApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: app, isLoading, isError } = useApplication(id ?? '');

  const senderName = user?.name ? user.name.split(' ')[0] : 'You';

  if (isLoading) {
    return (
      <DashboardLayout showBack backLabel="← Back to Dashboard" backTo="/dashboard">
        <div className="flex items-center justify-center h-64 text-hint text-sm">Loading application…</div>
      </DashboardLayout>
    );
  }

  if (isError || !app) {
    return (
      <DashboardLayout showBack backLabel="← Back to Dashboard" backTo="/dashboard">
        <div className="flex items-center justify-center h-64 text-red-500 text-sm">Failed to load application.</div>
      </DashboardLayout>
    );
  }

  const steps = getTimeline(app);
  const sentPct = app.outreach_count > 0 ? Math.round((app.sent_count / app.outreach_count) * 100) : 0;
  const bouncedCount = app.outreaches.filter(o => o.status === 'bounced').length;
  const followUpCount = app.outreaches.filter(o => o.status === 'sent' || o.status === 'replied').length;

  return (
    <DashboardLayout showBack backLabel="← Back to Dashboard" backTo="/dashboard">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1
            className="text-[26px] sm:text-[30px] font-semibold text-ink leading-snug"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            {app.company} — {app.role}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={app.status} />
            <span className="text-sm text-muted">{app.domain}</span>
          </div>
        </div>
        <a
          href="https://mail.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-ink text-white rounded-input text-xs sm:text-sm font-semibold hover:bg-black transition-colors"
        >
          Open All Drafts in Gmail ↗
        </a>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-5 gap-6">
        {/* Recruiter Outreaches card — on mobile, displayed FIRST so user sees contacts immediately */}
        <div className="order-1 lg:order-2 lg:col-span-2 space-y-5">
          {/* Outreach list */}
          <div className="bg-card border border-border rounded-card p-5 sm:p-6">
            <h3 className="text-[11px] font-semibold text-hint uppercase tracking-[1.2px] mb-3">
              All Outreaches ({app.outreach_count})
            </h3>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {app.outreaches.length === 0 ? (
                <p className="text-sm text-hint text-center py-6">No outreaches yet</p>
              ) : (
                app.outreaches.map(o => (
                  <div key={o.id} className="flex items-start justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink leading-snug">{o.recruiter_name}</p>
                      <p className="text-xs text-muted mt-0.5 leading-relaxed">{o.title}</p>
                    </div>
                    <div className="flex-shrink-0 pt-0.5">
                      <StatusBadge status={o.status} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Application details */}
          <div className="bg-card border border-border rounded-card p-5 sm:p-6">
            <h3 className="text-[11px] font-semibold text-hint uppercase tracking-[1.2px] mb-4">Application Details</h3>
            <div className="space-y-3">
              {[
                { label: 'Company', value: `${app.company} (${app.domain})` },
                { label: 'Role', value: app.role },
                { label: 'Date Applied', value: app.date },
                { label: 'Trigger', value: 'Automatic' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-start gap-2 text-sm">
                  <span className="text-muted flex-shrink-0">{label}</span>
                  <span className="text-ink font-medium text-right">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Follow-up card */}
          <div className="bg-card border border-border rounded-card p-5">
            <h3 className="text-[11px] font-semibold text-hint uppercase tracking-[1.2px] mb-2">Follow Ups</h3>
            <p className="text-xs text-muted leading-relaxed">
              Follow ups send automatically 3 days after each email is sent.
            </p>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <div className="h-2 w-2 rounded-full bg-amber-400" />
              <span className="text-muted">
                <span className="font-semibold text-ink">{followUpCount}</span> of {app.sent_count} sent emails eligible for follow ups
              </span>
            </div>
          </div>
        </div>

        {/* Progress, Timeline & Sample Draft — order-2 on mobile, left 60% on desktop */}
        <div className="order-2 lg:order-1 lg:col-span-3 space-y-5">
          {/* Progress */}
          <div className="bg-card border border-border rounded-card p-5 sm:p-6">
            <h3 className="text-[11px] font-semibold text-hint uppercase tracking-[1.2px] mb-4">Outreach Progress</h3>
            <div className="mb-3">
              <div className="flex justify-between text-xs text-muted mb-1.5 font-medium">
                <span>{app.sent_count} of {app.outreach_count} sent</span>
                <span>{sentPct}%</span>
              </div>
              <div className="h-2 rounded-full bg-border overflow-hidden">
                <div
                  className="h-full bg-g rounded-full transition-all"
                  style={{ width: `${sentPct}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Drafted', value: app.draft_count, color: 'text-amber-600' },
                { label: 'Sent', value: app.sent_count, color: 'text-blue-600' },
                { label: 'Replied', value: app.replied_count, color: 'text-emerald-600' },
                { label: 'Bounced', value: bouncedCount, color: 'text-red-500' },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center p-3 bg-bg border border-border/50 rounded-card">
                  <p
                    className={`text-2xl font-bold ${color}`}
                    style={{ fontFamily: 'Instrument Serif, serif' }}
                  >
                    {value}
                  </p>
                  <p className="text-xs font-medium text-muted mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-card border border-border rounded-card p-5 sm:p-6">
            <h3 className="text-[11px] font-semibold text-hint uppercase tracking-[1.2px] mb-4">Application Status</h3>
            <div className="space-y-4">
              {steps.map(({ label, done, detail, pending }, i) => (
                <div key={label} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        done ? 'bg-g-light text-g border border-g-border' : pending ? 'bg-amber-100 text-amber-700' : 'bg-bg border border-border text-hint'
                      }`}
                    >
                      {done ? '✓' : pending ? '⏳' : '○'}
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`w-px flex-1 mt-1 mb-1 min-h-[16px] ${done ? 'bg-g-border' : 'bg-border'}`} />
                    )}
                  </div>
                  <div className="pb-3">
                    <p className={`text-sm font-semibold ${done ? 'text-ink' : 'text-hint'}`}>{label}</p>
                    {detail && <p className="text-xs text-muted mt-0.5">{detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sample draft with dynamic signature */}
          <div className="bg-card border border-border rounded-card p-5 sm:p-6">
            <h3 className="text-[11px] font-semibold text-hint uppercase tracking-[1.2px] mb-1">Sample Draft</h3>
            <p className="text-xs text-muted mb-4">This is how your outreach looks</p>
            <div className="bg-bg rounded-card border border-border p-4">
              <div className="border-b border-border pb-3 mb-3">
                <p className="text-[10px] text-hint uppercase tracking-[1.2px] font-semibold mb-0.5">Subject</p>
                <p className="text-sm font-medium text-ink">
                  Quick note after applying for {app.role} at {app.company}
                </p>
              </div>
              <pre className="text-sm text-ink whitespace-pre-wrap font-sans leading-relaxed text-[13px]">
                {sampleEmailBody(app.company, app.role, senderName)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
